import React from 'react';

function RecommendationCategory({
  category,
  schools,
  savedCollegeIds,
  aiRecommendedSchoolIds,
  showCount,
  isLoadingMore,
  loadingAIRecommendations,
  filterAndSortSchools,
  onLoadMore,
  renderPreferenceButtons,
  renderSchoolLink,
  schoolPreferences,
  schoolExplanations,
  hoveredSchool,
  setHoveredSchool
}) {
  // Get category-specific values
  const categoryConfig = {
    reach: {
      header: 'Reach Schools',
      description: 'More competitive options. Your scores are slightly below the middle range, but still within reach.',
      loadMoreText: 'View 9 More Reach Schools',
      loadingText: 'AI is analyzing and ranking your best Reach schools...',
      cardClass: 'reach-card',
      headerClass: 'reach-header',
      sectionId: 'reach-section'
    },
    target: {
      header: 'Target Schools',
      description: 'Your scores fall in the middle 50% range. Competitive but realistic admission chances.',
      loadMoreText: 'View 9 More Target Schools',
      loadingText: 'AI is analyzing and ranking your best Target schools...',
      cardClass: 'target-card',
      headerClass: 'target-header',
      sectionId: 'target-section'
    },
    safety: {
      header: 'Safety Schools',
      description: 'Your test scores are above the 75th percentile. You have a strong chance of admission.',
      loadMoreText: 'View 9 More Safety Schools',
      loadingText: 'AI is analyzing and ranking your best Safety schools...',
      cardClass: 'safety-card',
      headerClass: 'safety-header',
      sectionId: 'safety-section'
    }
  };

  const config = categoryConfig[category];

  // Filter and sort schools
  const filteredSchools = filterAndSortSchools(schools, category);
  const aiRecommendedSchools = filteredSchools.filter(
    s => aiRecommendedSchoolIds?.includes(s.id) && !savedCollegeIds.has(s.id)
  );
  const displaySchools = aiRecommendedSchools.slice(0, showCount);

  // Show "View More" button if:
  // 1. There are more AI-recommended schools than currently shown, OR
  // 2. There are more schools in the category that could be AI-ranked
  const hasMore = aiRecommendedSchools.length > showCount ||
                  (filteredSchools.length > showCount && aiRecommendedSchoolIds && aiRecommendedSchoolIds.length > 0);

  return (
    <div id={config.sectionId} className="school-category">
      <h3 className={`category-header ${config.headerClass}`}>
        {config.header}
      </h3>
      <p className="category-description">
        {config.description}
      </p>

      {loadingAIRecommendations ? (
        <div className="loading-section">
          <div className="ai-loading-header">
            <div className="loading-spinner"></div>
            <p className="ai-loading-text">{config.loadingText}</p>
          </div>
          <div className="schools-grid">
            {[1, 2, 3].map((i) => (
              <div key={i} className={`modern-school-card skeleton-card ${config.cardClass}`}>
                <div className="skeleton-content">
                  <div className="skeleton-header"></div>
                  <div className="skeleton-stats-row">
                    <div className="skeleton-stat-item"></div>
                    <div className="skeleton-stat-item"></div>
                    <div className="skeleton-stat-item"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : displaySchools.length > 0 ? (
        <>
          <div className="schools-grid-modern">
            {displaySchools.map((school) => {
              const preference = schoolPreferences[school.id] || 'neutral';
              const explanation = schoolExplanations[`${category}_${school.id}`];
              
              // Calculate chance color based on value
              let chanceColorClass = 'chance-neutral';
              if (school.admission_chances >= 70) chanceColorClass = 'chance-high';
              else if (school.admission_chances >= 40) chanceColorClass = 'chance-medium';
              else if (school.admission_chances < 40 && school.admission_chances > 0) chanceColorClass = 'chance-low';

              return (
                <div key={school.id} className={`modern-school-card ${config.cardClass} preference-${preference}`}>
                  <div className="card-top-accent"></div>
                  
                  <div className="card-header-row">
                     <div className="school-identity">
                        <h4 className="school-name">{school.name}</h4>
                        <p className="school-location">{school.city}, {school.state}</p>
                     </div>
                     <div className="card-actions-top">
                        {renderPreferenceButtons(school, category)}
                     </div>
                  </div>

                  {explanation && (
                    <div className="ai-insight-box">
                      <span className="ai-icon">✨</span>
                      <p className="ai-text">{explanation}</p>
                    </div>
                  )}

                  <div className="stats-container">
                    <div className="stat-item">
                      <span className="stat-label">Acceptance</span>
                      <span className="stat-value">
                        {school.acceptance_rate ? `${school.acceptance_rate.toFixed(1)}%` : 'N/A'}
                      </span>
                    </div>
                    
                    <div className="stat-item">
                      <span className="stat-label">SAT Range</span>
                      <span className="stat-value">{school.sat_range || 'N/A'}</span>
                    </div>

                    <div className="stat-item">
                      <span className="stat-label">ACT Range</span>
                      <span className="stat-value">{school.act_range || 'N/A'}</span>
                    </div>
                  </div>

                  <div className={`admission-chance-bar ${chanceColorClass}`}>
                    <div className="chance-info">
                      <span className="chance-label">Your Chance</span>
                      <span className="chance-value">
                        {(school.admission_chances !== null && school.admission_chances !== undefined)
                          ? `${school.admission_chances}%`
                          : 'N/A'}
                      </span>
                    </div>
                    <div className="chance-progress-bg">
                       <div 
                         className="chance-progress-fill" 
                         style={{ width: `${school.admission_chances || 0}%` }}
                       ></div>
                    </div>
                  </div>

                  {renderSchoolLink(school)}
                </div>
              );
            })}
          </div>
          {hasMore && (
            <div className="load-more-container">
              <button
                className="load-more-btn"
                onClick={() => onLoadMore(category)}
                disabled={isLoadingMore}
              >
                {isLoadingMore ? 'Loading...' : config.loadMoreText}
              </button>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

export default RecommendationCategory;
